import React, {
  FunctionComponent,
  memo,
  useCallback,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";

import { usePackageEditorSetting } from "../../../hooks/usePackageEditorSetting";
import { useGetVehicleMappingByWheelId } from "../../../queries/useGetVehicleMappingByWheelId";
import { useCartClear } from "../../../stores/hooks/useCartClear";
import { usePackageEditor } from "../../../stores/hooks/usePackageEditor";
import {
  HashtagString,
  createHashStateHook,
} from "../../../stores/useHashState";
import { Dialog } from "../../../ui/Dialog/Dialog";
import { DialogHeader } from "../../../ui/Dialog/DialogHeader";
import { TabPanels, TabPanelsContext } from "../../../ui/TabPanels/TabPanels";
import { useOrderDetails } from "../../Cart/useOrderDetails";
import { VehicleMapping } from "../VehicleMapping/VehicleMapping";
import { useVehicleMaping } from "../useVehicleMaping";
import { useVehicleMappingCriteria } from "../useVehicleMappingCriteria";
import { Footer } from "./Footer";
import { NewPackageTabs } from "./NewPackageTabs";
import { PackageInfo } from "./PackageInfo";
import { PartList } from "./PartList";
import { getNewPackageId } from "./getNewPackageId";
import { selectPackageInfo } from "./selectPackageInfo";

const newPackageHash: HashtagString = "#new-package";

export const useNewPackageDialog = createHashStateHook(newPackageHash);

const Uncached: FunctionComponent = () => {
  const { t } = useTranslation();
  const tabsContextRef = useRef<TabPanelsContext>();
  const [visible, { hide }] = useNewPackageDialog();
  const { data: orderDetails } = useOrderDetails();
  const [isSending, setIsSending] = useState(false);
  const cartClear = useCartClear();
  const hasPackageEditor = usePackageEditorSetting();

  const packageInfo = selectPackageInfo(orderDetails);

  const [
    {
      customParts,
      modelsAreLoading,
      partListTabIndex,
      tabIndex,
      vehicleManufacturerIds,
      vehicleManufacturers,
      vehicleMapping,
      vehicleYearIds,
    },
    {
      addCustomPart,
      removeCustomPart,
      setModelsAreLoading,
      setPartListTabIndex,
      setTabIndex,
      setVehicleManufacturers,
      setVehicleMapping,
      toggleVehicleManufacturerId,
      toggleVehicleYearId,
      updateCustomPart,
    },
  ] = usePackageEditor(getNewPackageId(orderDetails));

  const selectTab = (index: number): void => {
    setTabIndex(index);

    if (tabsContextRef.current) {
      tabsContextRef.current.setIndex(index);
    }
  };

  const [criteria, setCriteria] = useVehicleMappingCriteria();
  const { data, isLoading } = useGetVehicleMappingByWheelId(
    packageInfo.wheelPartId ?? null
  );

  const mappingData = useVehicleMaping({
    criteria,
    fallbackManufacturers: vehicleManufacturers,
    partId: packageInfo.wheelPartId,
    setVehicleMapping,
    vehicleDetails: data?.mappedVehicles ?? null,
    vehicleMapping,
  });

  const handleClose = useCallback(() => {
    hide();
    setCriteria("");
  }, [hide, setCriteria]);

  if (!hasPackageEditor) {
    return null;
  }

  return (
    <Dialog
      className="md:w-[32rem]"
      id="new-package-dialog"
      onClose={handleClose}
      placement="top"
      visible={visible}
    >
      <DialogHeader
        onClose={handleClose}
        title={t("packageEditor.newPackage")}
      />

      {orderDetails && packageInfo.isValid ? (
        <>
          <NewPackageTabs
            data={mappingData}
            selectTab={selectTab}
            tabIndex={tabIndex}
          />
          <TabPanels contextRef={tabsContextRef} defaultIndex={0}>
            <PartList
              addCustomPart={addCustomPart}
              customParts={customParts}
              orderDetails={orderDetails}
              partListTabIndex={partListTabIndex}
              removeCustomPart={removeCustomPart}
              setPartListTabIndex={setPartListTabIndex}
              updateCustomPart={updateCustomPart}
            />
            <VehicleMapping
              criteria={criteria}
              data={mappingData}
              isLoading={isLoading}
              setCriteria={setCriteria}
              setModelsAreLoading={setModelsAreLoading}
              setVehicleManufacturers={setVehicleManufacturers}
              toggleVehicleManufacturerId={toggleVehicleManufacturerId}
              toggleVehicleYearId={toggleVehicleYearId}
              vehicleManufacturerIds={vehicleManufacturerIds}
              vehicleYearIds={vehicleYearIds}
            />
            <PackageInfo
              reset={(): void => {
                handleClose();
                selectTab(0);
                cartClear();
              }}
              customParts={customParts}
              data={mappingData}
              orderDetails={orderDetails}
              quoteId={orderDetails?.orderId}
              setIsSending={setIsSending}
            />
          </TabPanels>
          <Footer
            hide={handleClose}
            isSending={isSending}
            modelsAreLoading={modelsAreLoading}
            partListTabIndex={partListTabIndex}
            selectTab={selectTab}
            selectedVehiclesCount={mappingData?.totalSelected || 0}
            shouldFetchVehicles={!mappingData?.vehicles.length}
            tabIndex={tabIndex}
            vehicleManufacturerIds={vehicleManufacturerIds}
            vehicleYearIds={vehicleYearIds}
          />
        </>
      ) : (
        <p className="p-4 pb-8">{t("packageEditor.invalidQuoteNotice")}</p>
      )}
    </Dialog>
  );
};

export const NewPackageDialog = memo(Uncached);
